import { useMemo } from "react";

import styled from "styled-components";

import { useNetworkMetadata, usePermissions } from "Flocknote/apiHooks";

import { useDuplicateCount } from "AccountMerge/duplicatesApi/queryHooks";
import NavLink from "NavBar/components/NavLink";
import NavLinksWrapper from "NavBar/components/NavLinksWrapper";
import NavSection from "NavBar/components/NavSection";
import useIsSelected from "NavBar/components/useIsSelected";
import {
  navSectionKeys,
  useActiveSectionKey,
  useDuplicateFinderLinkRef,
  useHouseholdDatabaseRef,
  useHouseholdDatabaseSettingsRef,
} from "NavBar/navBarStore";
import {
  useIsDuplicateFinderLinkFlashing,
  useIsHouseholdDatabaseSettingsFlashing,
  useIsHouseholdDatabaseSquiggleFlashing,
} from "NavBar/stateHooks";

import { navColors } from "UI/colors";

import SettingsContent from "./SettingsContent";

// The link only goes to one tab of the Data Tools page; stay selected on both
const DATA_CLEANUP_ROUTES = [
  "/householddatabase/changelog",
  "/householddatabase/duplicates",
];

const useSettingsSelected = () => {
  const isStatusesSelected = useIsSelected("/householddatabase/statuses");
  const isProfileFieldsSelected = useIsSelected(
    "/householddatabase/profilefields"
  );
  return Boolean(isStatusesSelected || isProfileFieldsSelected);
};

const HouseholdDatabase = () => {
  const [activeSectionKey] = useActiveSectionKey();
  const isActive = activeSectionKey === navSectionKeys.database;

  const [
    {
      canSeeSubnetworksNav,
      canSeeParishInsightsNav,
      canSeeFamilyRegistrationSetup,
      canSeeHouseholdDatabaseNav,
      canSeeHealthReportNav,
      canSeeDuplicateFinder,
      canSeeChangeLog,
      canSeeDatabaseSetup,
      canSeeProfileFieldsSetup,
    },
  ] = usePermissions();
  const [{ isCatholic }] = useNetworkMetadata();
  const duplicateCount = useDuplicateCount();
  const settingsSelected = useSettingsSelected();
  const householdDatabaseRef = useHouseholdDatabaseRef();
  const householdDatabaseSettingsRef = useHouseholdDatabaseSettingsRef();
  const isSquiggleFlashing = useIsHouseholdDatabaseSquiggleFlashing();
  const isSettingsFlashing = useIsHouseholdDatabaseSettingsFlashing();
  const isDuplicateFinderFlashing = useIsDuplicateFinderLinkFlashing();
  const duplicateFinderLinkRef = useDuplicateFinderLinkRef();

  const showDataCleanup = canSeeChangeLog || canSeeDuplicateFinder;
  // the page's default tab is the change log; fall back for admins who can
  // only see the duplicate finder
  const dataCleanupRoute = canSeeChangeLog
    ? "householddatabase/changelog"
    : "householddatabase/duplicates";
  const showSettings = canSeeDatabaseSetup || canSeeProfileFieldsSetup;

  const firstVisibleLink = useMemo(() => {
    if (canSeeHouseholdDatabaseNav) return "/householddatabase";
    if (canSeeParishInsightsNav) return "/parishes";
    if (canSeeHealthReportNav) return "/healthreport";
    if (canSeeSubnetworksNav) return "/subnetworks";
    if (canSeeFamilyRegistrationSetup)
      return "/householddatabase/familyregistration";
    if (showDataCleanup) return `/${dataCleanupRoute}`;
    return null;
  }, [
    canSeeHouseholdDatabaseNav,
    canSeeParishInsightsNav,
    canSeeHealthReportNav,
    canSeeSubnetworksNav,
    canSeeFamilyRegistrationSetup,
    showDataCleanup,
    dataCleanupRoute,
  ]);

  return (
    <SNavSection
      $isActive={isActive}
      label="Household Database"
      data-cy="NavBar_HouseholdDatabaseMenu"
      navSectionKey={navSectionKeys.database}
      color={navColors.database}
      icon="database far"
      SettingsContent={showSettings ? SettingsContent : undefined}
      settingsSelected={settingsSelected}
      headerTextRef={householdDatabaseRef}
      settingsButtonRef={householdDatabaseSettingsRef}
      isSquiggleFlashing={isSquiggleFlashing}
      isSettingsFlashing={isSettingsFlashing}
      defaultRoute={firstVisibleLink}
    >
      <NavLinksWrapper>
        {canSeeHouseholdDatabaseNav && (
          <NavLink
            to="householddatabase"
            data-cy="NavBar_HouseholdDatabase"
            icon="fa-duotone fa-solid fa-database fa-swap-opacity"
            navSectionKey={navSectionKeys.database}
          >
            Database
          </NavLink>
        )}
        {canSeeParishInsightsNav && (
          <NavLink
            to="parishes"
            data-cy="NavBar_ParishInsights"
            icon="church fad"
            navSectionKey={navSectionKeys.database}
          >
            {`${isCatholic ? "Parish" : "Church"} Insights`}
          </NavLink>
        )}
        {canSeeHealthReportNav && (
          <NavLink
            to="healthreport"
            data-cy="NavBar_HealthReport"
            icon="fa-duotone fa-solid fa-diagram-project"
            navSectionKey={navSectionKeys.database}
          >
            {`${isCatholic ? "Parish" : "Church"} Insights`}
          </NavLink>
        )}
        {canSeeSubnetworksNav && (
          <NavLink
            to="subnetworks"
            data-cy="NavBar_Subnetworks"
            icon="sitemap fad"
            navSectionKey={navSectionKeys.database}
          >
            Subnetworks
          </NavLink>
        )}
        {canSeeFamilyRegistrationSetup && (
          <NavLink
            to="householddatabase/familyregistration"
            data-cy="NavBar_FamilyRegistration"
            icon="fa-duotone fa-solid fa-address-book fa-swap-opacity"
            navSectionKey={navSectionKeys.database}
          >
            Family Registration
          </NavLink>
        )}
        {showDataCleanup && (
          <NavLink
            ref={duplicateFinderLinkRef}
            to={dataCleanupRoute}
            alsoSelectedOn={DATA_CLEANUP_ROUTES}
            data-cy="NavBar_DuplicateFinder"
            icon="wand-magic-sparkles fad"
            count={duplicateCount}
            navSectionKey={navSectionKeys.database}
            showCount
            isFlashing={isDuplicateFinderFlashing}
          >
            Data Tools
          </NavLink>
        )}
      </NavLinksWrapper>
    </SNavSection>
  );
};

const SNavSection = styled(NavSection)`
  padding-bottom: ${({ $isActive }) => ($isActive ? "4px" : "0")};
  transition: padding-bottom 300ms;
`;

export default HouseholdDatabase;
